import { DatabaseModel, ModelJson } from './database-model';
import "reflect-metadata";
import { SignupMotive, StatEvent, StatEventMoteUnlocked, StatEventVoucherPurchased, StatType } from './stat-event';

export enum AccumulatedStatsType {
    day = 'day',
    month = 'month',
    year = 'year',
    day_total = 'day_total',
    month_total = 'month_total',
    year_total = 'year_total',
    live = 'live',
    corrections = 'corrections',
}

type AccumulateType = 'addition' | 'average' | 'map' | 'list';
const ACCUMULATE_DATA = Symbol("ACCUMULATE_DATA");

/**
 * Define decorator to register meta data of member properties to be exported to JSON and how to accumulate them
 */
function Accumulate(accumulateType: AccumulateType, averageCountProperty?: string) {
    return Reflect.metadata(ACCUMULATE_DATA, { type: accumulateType, averageCountProperty: averageCountProperty });
}

/**
 * Get decorator meta data of member property
 */
function getAccumulationType(target: any, propertyName: string): { type: AccumulateType, averageCountProperty?: string; } {
    return Reflect.getMetadata(ACCUMULATE_DATA, target, propertyName) || {};
}

export class AccumulatedStats extends DatabaseModel {
    public key: string;
    public type: AccumulatedStatsType = AccumulatedStatsType.day;
    // The time the AccumulatedStats entry was created
    public createdOn: any | Date = null;
    // Start time of the period that this accumulated stat entry accounts for
    public statsStartDate: any | Date = null;

    // Project key of accumulated events
    public projectKey: string = "";
    // Tour key / keys (separated with ,) of accumulated events
    @Accumulate('list')
    public tourKey: string = "";
    // Total number of stat events accumulated
    @Accumulate('addition')
    public totalStatEvents: number = 0;

    // Number of users that created a StoryHunt Creator account
    @Accumulate('addition')
    public creatorAccounts: number = 0;
    // Number of users that showed intention to create a StoryHunt Creator account (signed up for news letter)
    @Accumulate('addition')
    public creatorSignedUp: number = 0;

    // Unique users landed in the app for the first time on a given project
    @Accumulate('addition')
    public firstProjectVisit: number = 0;
    // A user logged in to an account
    @Accumulate('addition')
    public userLogins: number = 0;
    // A user logged out of an account
    @Accumulate('addition')
    public userLogouts: number = 0;
    // Unique active user aka. users unlocking at least one story (first unlock on any tour/project)
    // Info: For 'activeTourUsers' meaning a user unlocked at least one story on a tour (first unlock on tour) see toursStarted
    @Accumulate('addition')
    public activeUsers: number = 0;
    // Unique tour page visits
    @Accumulate('addition')
    public firstTourPageVisits: number = 0;
    // All (non unique) tour page visits
    @Accumulate('addition')
    public tourPageVisits: number = 0;
    // Tour started aka. first story unlocked or 'activeTourUsers'
    @Accumulate('addition')
    public toursStarted: number = 0;
    // Tour completed aka. all stories on the tour was unlocked
    @Accumulate('addition')
    public toursCompleted: number = 0;
    // Number of stories unlocked by the user
    @Accumulate('addition')
    public storiesUnlocked: number = 0;
    // Unlocks attributed to this tour via EXTRA_MOTE_UNLOCKED (not counted in storiesUnlocked)
    @Accumulate('addition')
    public extraMoteUnlocks: number = 0;
    // Number of (unlocked) stories the user opened
    @Accumulate('addition')
    public storiesOpened: number = 0;
    // Number of tours / tour pages in the app that the user opened
    @Accumulate('addition')
    public toursOpened: number = 0;
    @Accumulate('average', 'mediaConsumedEvents')
    // Average percentage of media consumed by the user
    public mediaConsumedAverage: number = 0;
    // Number of media elements listened to (at least 80%) by the user
    public mediaConsumedEvents: number = 0;

    // Number of directions / navigate to clicked
    @Accumulate('addition')
    public directionsClicked: number = 0;
    // Number of times the journey plan/ Rejseplanen was loaded
    @Accumulate('addition')
    public journeyLoads: number = 0;
    // Number times stories was loved (deprecated feature)
    @Accumulate('addition')
    public storiesLoved: number = 0;

    // Number of tour reviews from users
    @Accumulate('addition')
    public tourReviews: number = 0;

    // Number of correct quiz answers
    @Accumulate('addition')
    public quizAnswersCorrect: number = 0;
    // Number of correct quiz answers
    @Accumulate('addition')
    public quizAnswersWrong: number = 0;
    // Number of survey responses
    @Accumulate('addition')
    public surveyResponse: number = 0;

    @Accumulate('average', 'tourDayCompleteRateWeight')
    // Average percentage of stories unlocked on tour (unlockedStories/storiesOnTour) over a day
    // OBS: Tours completed over several days will lower the complete rate
    public tourDayCompleteRate: number = 0;
    // Number of tour completed rates summed to generate weighted average
    public tourDayCompleteRateWeight: number = 0;
    // Number of unlocked stories per active user in average weighted with toursStarted (tourUser)
    @Accumulate('average', 'unlockedStoriesPerUserWeight')
    public unlockedStoriesPerUser: number = 0;
    public unlockedStoriesPerUserWeight: number = 0;
    // Total number of stories on tour / accumulated potential stories to be unlocked on tours
    @Accumulate('addition')
    public totalTourStories: number = 0;

    /**
     * Voucher stats
     */
    @Accumulate('addition')
    public vouchersPurchased: number = 0;
    @Accumulate('addition')
    public vouchersClaimed: number = 0;
    @Accumulate('addition')
    public vouchersRedeemed: number = 0;
    @Accumulate('addition')
    public vouchersRevenue: number = 0;
    @Accumulate('addition')
    public partnerCost: number = 0;
    @Accumulate('addition')
    public vouchersExpired: number = 0;
    // Map with number of products sold per product
    @Accumulate('map')
    public productsAmountMap: { [productKey: string]: number; } = {};
    // Map with revenue accumulated per product
    @Accumulate('map')
    public productsRevenueMap: { [productKey: string]: number; } = {};
    // Vouchers purchased but not payed due to errors or customer canceled purchase
    @Accumulate('addition')
    public vouchersNotPayed: number = 0;

    /** Project (non tour) specific stats */
    @Accumulate('addition')
    // Number of StoryHunt accounts created (with login)
    public accountCreated: number = 0;
    @Accumulate('addition')
    // Number of new users created (including anonymous logins)
    public userCreated: number = 0;

    @Accumulate('addition')
    // Number of user login errors detected
    public loginErrors: number = 0;

    /** Stats implementation errors */
    // Total number of events not counted in the results
    @Accumulate('addition')
    public ignoredEvents: number = 0;
    // Map of events ignored / not counted in the stat result
    @Accumulate('map')
    public ignoredEventMap: { [eventKey: string]: boolean; } = {};
    // Number of stat events corrected (with non existing moteKey / projectKey etc.)
    @Accumulate('addition')
    public correctedStatEvents: number = 0;
    @Accumulate('map')
    public nonExistingTourKeyStats: { [eventKey: string]: boolean; } = {};

    constructor(json?: ModelJson, key?: string) {
        super(json, key);
        this.fromJson(json);
        this.key = key || json?.key;
    };

    /**
    * Apply data from database JSON to this DatabaseModel instance.
    */
    public fromJson(json: ModelJson): void {
        if(!json) return;

        this.key = json.key;
        this.type = json.type || AccumulatedStatsType.day;
        this.projectKey = json.projectKey || '';
        this.createdOn = this.firestoreTimestampToDate(json.createdOn);
        this.statsStartDate = this.firestoreTimestampToDate(json.statsStartDate);

        // Get registered accumulation properties from json
        for(const property of this.getAccumulationMembers()) {
            switch(property.type) {
                case 'addition':
                    this[property.name] = json[property.name] || 0;
                    break;
                case 'average':
                    this[property.name] = json[property.name] || 0;
                    this[property.averageCountProperty] = json[property.averageCountProperty] || 0;
                    break;
                case 'list':
                    this[property.name] = json[property.name] || '';
                    break;
                case 'map':
                    this[property.name] = json[property.name] || {};
                    break;
            }
        }
    }

    /**
     * Convert this object to a serializable JSON compatible object.
     */
    public toJson(): ModelJson {
        // Run through all properties
        let json = {};
        Object.assign(json, this);
        // Delete empty fiels (do not store this overhead in all events)
        for(let propName in json) {
            // Check for undefined properties
            const isUndefined = typeof json[propName] == 'undefined' || json[propName] == null;
            // Check for empty object
            const isEmptyObject = !isUndefined && !json[propName].getDate && (typeof json[propName] == 'object' && Object.keys(json[propName]).length == 0);

            // Only keep properties with a value (except 0 and empty object to save bandwidth / database space)
            if(isUndefined || isEmptyObject || json[propName] == 0) {
                // Delete empty from json
                delete json[propName];
            }
        }
        return json;
    }

    private getAccumulationMembers(): { name: string, type: AccumulateType, averageCountProperty?: string; }[] {
        // List all class members and get accumulation type
        const propertyList = Object.getOwnPropertyNames(this).map((name) => {
            // Get data from meta store
            const { type, averageCountProperty } = getAccumulationType(this, name);
            return { name: name, type: type, averageCountProperty: averageCountProperty };
        });
        return propertyList.filter(prop => Boolean(prop?.type));
    }

    /**
     * Accumulate / add data from a stats object to this stats object.
     */
    public addStats(stats: AccumulatedStats): AccumulatedStats {
        // Add additional stats object to the registered accumulation members of this Stats object
        for(const property of this.getAccumulationMembers()) {
            switch(property.type) {
                case 'addition':
                    // Add numbers of this stat event with addition stats
                    this[property.name] += stats[property.name];
                    break;
                case 'average':
                    // Add addition stats to this stats using weighted average
                    this[property.name] = this.calcWeightedAverage(this[property.name], this[property.averageCountProperty], stats[property.name], stats[property.averageCountProperty]);
                    this[property.averageCountProperty] += stats[property.averageCountProperty];
                    break;
                case 'list':
                    if(!stats[property.name]) break;
                    // Add the addition stat event to the existing comma separated list list (or just use the value from the addition stat)
                    this[property.name] ? this[property.name] += `,${stats[property.name]}` : this[property.name] = stats[property.name];
                    break;
                case 'map':
                    // Assign / accumulate additional stat properties to the current map
                    this[property.name] = this.addMaps([this[property.name], stats[property.name]]);
                    break;
            }
        }
        return this;
    }

    /**
     *  Assign / accumulate properties from one or mote object maps into one
     */
    private addMaps(maps: { [key: string]: any; }[]): { [key: string]: any; } {
        return maps.reduce((a, b) => {
            for(let property in b) {
                // If this is a map of numbers, calculate the sum of the two objects
                if(typeof b[property] == "number") {
                    a[property] = (a[property] || 0) + b[property];
                }
                // If it is a binary map (boolean) or other type just assign value from all maps to sum map
                else {
                    a[property] = b[property];
                }
            }
            return a;
        }, {});
    }

    /**
     * Perform a weighted running average addition of to average values.
     */
    private calcWeightedAverage(currentAverage: number, currentEvents: number, statAverage: number, statEvents: number): number {
        // Do nothing if there is not addition to the average in the additional stat event
        if(!statEvents) return currentAverage;
        // Count all events
        const totalEvents = currentEvents + statEvents;
        // Calculate weighted part of current stats and stat addition
        const currentWeighted = (currentAverage / totalEvents) * currentEvents;
        const statWeighted = (statAverage / totalEvents) * statEvents;
        // Return total weighted average
        return currentWeighted + statWeighted;
    }

    /**
    * Create an accumulated stats object for a list of stat events of a tour
    * @param startTime Start time of the accumulated stat data period.
    * @param tourKey The key of the tour that the accumulated stat event is associated with.
    * @param events List of tour stat events to be accumulated.
    */
    public static createAccumulatedTourStats(startTime: Date, tourKey: string, events: StatEvent[], accumulatedKey: string = ""): AccumulatedStats {
        // Init new accumulated stats object for this tour
        let accumulated = new AccumulatedStats({}, accumulatedKey);
        accumulated.createdOn = Date.now();
        accumulated.tourKey = tourKey;
        accumulated.statsStartDate = startTime;
        accumulated.totalStatEvents = events.length;

        // Here comes a bit of a hacky workaround to find the correct project key that might be wrong in some stats (because of some bug, but we need to work around to register previous events correctly)
        // Find the first project key in tour event using any VOUCHER_PURCHASED events (these events always has correct projectKey)
        accumulated.projectKey = events.find( ev => (ev.projectKey && ev.statType == StatType.VOUCHER_PURCHASED))?.projectKey || "";
        // If we dident find the a project key from purchase events, try with the other stat types even though they might contain the wrong project key, like default
        if(!accumulated.projectKey) accumulated.projectKey = events.find( ev => (ev.projectKey && ev.projectKey != "default"))?.projectKey || "default";

        // Run through each event and accumulate stats
        events.forEach(ev => AccumulatedStats.addStatEvent(ev, accumulated));
        // Generate tour specific events based on all accumulated stats of the period
        accumulated = AccumulatedStats.updatePeriodStats(accumulated);
        return accumulated;
    }

    /**
     * Generate tour specific events based on all accumulated stats of the period
     */
    private static updatePeriodStats(accumulated: AccumulatedStats): AccumulatedStats {
        if(accumulated.toursStarted > 0) {
            // Calculate the number of unlocks per 'user' aka users first tour unlock aka tour started
            accumulated.unlockedStoriesPerUser = (accumulated.storiesUnlocked / accumulated.toursStarted);
            // Use tours started aka active tour users as average weight
            accumulated.unlockedStoriesPerUserWeight += accumulated.toursStarted;
        }

        if(accumulated.toursStarted > 0 && accumulated.totalTourStories > 0) {
            // Calculate percentage of how many unlocks each user made out of the potential total unlocks on the tour
            accumulated.tourDayCompleteRate = (accumulated.unlockedStoriesPerUser / accumulated.totalTourStories) * 100;
            // Use numbers of unlocks as weight for average
            accumulated.tourDayCompleteRateWeight += accumulated.storiesUnlocked;
        }
        return accumulated;
    }

    /**
     * Add a stat event to the accumulated stats object count
     */
    private static addStatEvent(ev: StatEvent, stats: AccumulatedStats): AccumulatedStats {
        // Fallback to take project key from the first event if it is not already set (which should have been done in caller, aka createAccumulatedTourStats)
        if(!stats.projectKey && ev.projectKey) stats.projectKey = ev.projectKey;

        switch(ev.statType) {
            case StatType.CREATOR_SIGNUP:
                // Count Creator sign up for e-mail and actual Creator accounts created
                (ev.addition?.signupMotive == SignupMotive.DIRECT_SIGNUP) ? stats.accountCreated++ : stats.creatorSignedUp++;
                break;

            case StatType.TOUR_PAGE_VISITED:
                // Count all tour page visits
                stats.tourPageVisits++;
                // Count all unique tour page visits
                stats.firstTourPageVisits += (ev.addition?.firstVisit) ? 1 : 0;
                break;

            case StatType.FIRST_UNLOCKED:
                // Count unique active user for this tour (OBS: first unlock is
                // only thrown once per project, so 'unique' see from a project perspective)
                stats.activeUsers++;
                // Count storiesUnlocked, toursStarted and toursCompleted
                stats = AccumulatedStats.countUnlockedEvent(ev, stats);
                break;

            case StatType.MOTE_UNLOCKED:
                // Count storiesUnlocked, toursStarted and toursCompleted
                stats = AccumulatedStats.countUnlockedEvent(ev, stats);
                break;

            case StatType.EXTRA_MOTE_UNLOCKED:
                stats.extraMoteUnlocks++;
                break;

            case StatType.TOUR_STARTED:
                // Explicit tour started event (alternative to inference from MOTE_UNLOCKED with tourStoriesUnlocked==1)
                stats.toursStarted++;
                break;
            case StatType.TOUR_COMPLETED:
                // Explicit tour completed event (alternative to inference from MOTE_UNLOCKED with tourStoriesUnlocked==totalTourStories)
                stats.toursCompleted++;
                break;

            case StatType.MEDIA_CONSUMED:
                // Count as consumed if more than 80% of media was consumed
                stats.mediaConsumedEvents++;
                // Make running average calculation (this is only an estimated average, but a fine estimate)
                const events = stats.mediaConsumedEvents;
                const consumePct = ev.addition?.consumedPct || 0;
                stats.mediaConsumedAverage = (stats.mediaConsumedAverage / events) * (events - 1) + (consumePct / events);
                break;

            case StatType.TOUR_OPENED:
                stats.toursOpened++;
                break;
            case StatType.MOTE_OPENED:
                stats.storiesOpened++;
                break;

            case StatType.QUIZ_ANSWER_RIGHT:
                stats.quizAnswersCorrect++;
                break;
            case StatType.QUIZ_ANSWER_WRONG:
                stats.quizAnswersWrong++;
                break;

            case StatType.MOTE_LOVED:
                stats.storiesLoved++;
                break;

            case StatType.TOUR_REVIEWED:
                stats.tourReviews++;
                break;

            case StatType.VOUCHER_PURCHASED:
                AccumulatedStats.countVoucherPurchased(ev, stats);
                break;

            case StatType.VOUCHER_CLAIMED:
                stats.vouchersClaimed++;
                break;

            case StatType.VOUCHER_REDEEMED:
                stats.vouchersRedeemed++;
                break;

            /**
             * Project specific stats...
             */
            case StatType.FIRST_PROJECT_VISIT:
                stats.firstProjectVisit++;
                break;
            case StatType.USER_LOGIN:
                stats.userLogins++;
                break;
            case StatType.USER_LOGOUT:
                stats.userLogouts++;
                break;

            case StatType.DIRECTIONS_CLICKED:
                stats.directionsClicked++;
                break;

            case StatType.JOURNEY_LOADED:
                stats.journeyLoads++;
                break;

            case StatType.ACCOUNT_CREATED:
                stats.accountCreated++;
                break;

            case StatType.USER_CREATED:
                stats.userCreated++;
                break;

            case StatType.USER_LOGIN_ERROR:
                stats.loginErrors++;
                break;

            default:
                // Add event to ignored account
                stats.ignoredEvents++;
                // Add event to map of ignored events
                stats.ignoredEventMap[ev.statType] = true;
                console.warn('[AccumulatedStats] ignored event:', ev.statType, { tourKey: ev.tourKey, projectKey: ev.projectKey });
                break;
        }
        return stats;
    }

    private static countVoucherPurchased(ev: StatEventVoucherPurchased, stats: AccumulatedStats) {
        // Count vouchers where payment did not succeed
        if(!ev.addition?.paymentSuccess) {
            console.log("Voucher not payed detected..");
            stats.vouchersNotPayed++;
            return;
        }

        // Count voucher
        stats.vouchersPurchased += (ev.addition?.amount || 0);
        // Add voucher revenue
        const totalPrice = ev.addition?.totalPrice || 0;
        stats.vouchersRevenue += totalPrice;
        stats.partnerCost += (ev.addition?.partnerCost || 0);
        const productKey = ev.addition?.productKey || 'unknownProduct';
        // Add product amount to map
        const productAmount = stats.productsAmountMap[productKey];
        const addProductAmount = ev.addition?.amount || 0;
        stats.productsAmountMap[productKey] = productAmount ? productAmount + addProductAmount : addProductAmount;
        // Add product revenue to map
        const productRevenue = stats.productsRevenueMap[productKey];
        stats.productsRevenueMap[productKey] = productRevenue ? productRevenue + totalPrice : totalPrice;
    }

    private static countUnlockedEvent(ev: StatEventMoteUnlocked, stats: AccumulatedStats): AccumulatedStats {
        // Count unlocked story
        stats.storiesUnlocked++;
        // Check if this event concerns the first unlocked story of the tour
        stats.toursStarted += (ev.addition?.tourStoriesUnlocked == 1) ? 1 : 0;
        // Check if this event concerns the last unlock of the tour
        const isAllStoriesUnlocked = (ev.addition?.tourStoriesUnlocked > 0 && ev.addition?.tourStoriesUnlocked == ev.addition?.totalTourStories);
        stats.toursCompleted += isAllStoriesUnlocked ? 1 : 0;
        // Set totalTourStories of the tour (this value should be the same for all unlock events on this tour)
        // OBS: totalTourStories might be missing on FIRST_UNLOCK (for some reason), therefor we need to check if the value exists
        if(!stats.totalTourStories && ev.addition?.totalTourStories > 0) stats.totalTourStories = ev.addition.totalTourStories || 0;
        return stats;
    }

    private firestoreTimestampToDate(timestamp: number | {seconds: number, nanoseconds: number}) : Date {
        if(!timestamp) return null;
        if(typeof timestamp == 'number') return new Date(timestamp);
        if(typeof timestamp == 'object' && timestamp.seconds) return new Date(timestamp.seconds * 1000);
        return null;
    }

    public static sumStats(statsList: AccumulatedStats[]): AccumulatedStats {
        return statsList.reduce( (sum, stats) => sum.addStats(stats), new AccumulatedStats());
    }
}
